import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { SwitchCamera } from "lucide-react";

const beep = (ok) => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.value = ok ? 880 : 200;
    g.gain.setValueAtTime(0.18, ctx.currentTime);
    o.start();
    o.stop(ctx.currentTime + (ok ? 0.12 : 0.32));
    o.onended = () => ctx.close();
  } catch (e) {}
};

export const QrScanner = ({ onScan, result, onClose }) => {
  const idRef = useRef(`qr-reader-${Math.random().toString(36).slice(2)}`);
  const lastRef = useRef({ token: "", at: 0 });
  const onScanRef = useRef(onScan);
  const [facing, setFacing] = useState("environment");

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    const scanner = new Html5Qrcode(idRef.current, { verbose: false });
    let cancelled = false;
    scanner
      .start({ facingMode: facing }, { fps: 10, qrbox: 240 },
        (decoded) => {
          const now = Date.now();
          if (decoded === lastRef.current.token && now - lastRef.current.at < 3000) return;
          lastRef.current = { token: decoded, at: now };
          onScanRef.current && onScanRef.current(decoded);
        },
        () => {})
      .catch(() => {});
    return () => {
      cancelled = true;
      try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch (e) {}
      void cancelled;
    };
  }, [facing]);

  useEffect(() => {
    if (!result || !result._ts) return;
    beep(result.valid);
    try { if (navigator.vibrate) navigator.vibrate(result.valid ? 80 : [60, 50, 60]); } catch (e) {}
  }, [result?._ts]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-eff">Scanner le QR du billet</div>
          <button data-testid="qr-switch-camera" title="Changer de caméra"
            onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
            className="text-muted-eff hover:text-white">
            <SwitchCamera className="w-4 h-4" />
          </button>
        </div>
        <div id={idRef.current} className="w-full overflow-hidden border border-[#292929]" />
        {result && (
          <div data-testid="scan-result"
            className={`mt-4 p-4 text-center border ${result.valid ? "border-green-500 bg-green-500/10" : "border-red-500 bg-red-500/10"}`}>
            <div className={`text-sm tracking-widest uppercase ${result.valid ? "text-green-400" : "text-red-400"}`}>
              {result.valid ? "Billet valide ✓" : `Refusé — ${result.reason || ""} ✗`}
            </div>
            {(result.buyer_name || result.buyer_email) && (
              <div className="text-white text-sm mt-1">{result.buyer_name || result.buyer_email}</div>
            )}
            {result.event_title && <div className="text-muted-eff text-xs mt-0.5">{result.event_title}</div>}
            {!result.valid && result.checked_in_at && (
              <div className="text-muted-eff text-xs mt-0.5">Déjà scanné le {new Date(result.checked_in_at).toLocaleString("fr-CA")}</div>
            )}
            {typeof result.checked_in_count === "number" && (
              <div className="text-white text-xs mt-2 tracking-widest">{result.checked_in_count} / {result.total} entrées</div>
            )}
          </div>
        )}
        <div className="text-[10px] text-muted-eff text-center mt-3">Le scan reste actif — présentez le billet suivant.</div>
        <button data-testid="qr-scanner-close" onClick={onClose}
          className="w-full mt-3 text-xs tracking-widest uppercase border border-[#292929] py-2.5 hover:border-white transition-colors">
          Fermer
        </button>
      </div>
    </div>
  );
};
